import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import { useTranslation } from 'next-i18next';
import toast from 'react-hot-toast';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  EmailShareButton,
} from 'react-share';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faXTwitter,
  faWhatsapp,
  faLinkedinIn,
  faTelegramPlane,
} from '@fortawesome/free-brands-svg-icons';
import {
  faTimes,
  /* faCode, */ faCopy,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';

type Props = {
  show: boolean;
  onClose?: () => void;
  contentSlug: string;
  contentTitle: string;
  contentType?: 'movie' | 'episode';
};

const ShareModal: React.FC<Props> = ({
  show = false,
  onClose,
  contentSlug,
  contentTitle,
  contentType = 'movie',
}) => {
  const { t } = useTranslation();
  // Comment out embed functionality
  // const [showEmbed, setShowEmbed] = useState(false);
  const [copyText, setCopyText] = useState(t('common:share.copy'));
  const [activeField, setActiveField] = useState(false);
  // const embedCodeRef = useRef<HTMLPreElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  // Generate share URLs
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  // For episodes, the contentSlug is already in the format: series-slug/season/number/episode/number
  const shareUrl =
    contentType === 'episode'
      ? `${baseUrl}/series/${contentSlug}`
      : `${baseUrl}/movie/${contentSlug}`;

  // For debugging - remove in production
  if (process.env.NODE_ENV !== 'production' && contentType === 'episode') {
    console.log('Episode share URL:', shareUrl);
  }

  // Embed code template
  // const embedCode =
  //   contentType === 'episode'
  //     ? `<iframe src="${baseUrl}/embed/series/${contentSlug}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`
  //     : `<iframe src="${baseUrl}/embed/${contentSlug}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`;

  const handleCopyLink = () => {
    if (linkInputRef.current) {
      linkInputRef.current.select();
      navigator.clipboard.writeText(shareUrl);
      setCopyText(t('common:share.copied'));
      setActiveField(true);

      setTimeout(() => {
        setCopyText(t('common:share.copy'));
        setActiveField(false);
      }, 3000);

      toast.success(t('common:share.copied'));
    }
  };

  // Commented out to fix unused variable linting error
  /* 
  const handleCopyEmbed = () => {
    if (embedCodeRef.current) {
      const range = document.createRange();
      range.selectNodeContents(embedCodeRef.current);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        navigator.clipboard.writeText(embedCode);
        toast.success(t('common:share.copied'));
      }
    } else {
      navigator.clipboard.writeText(embedCode);
      toast.success(t('common:share.copied'));
    }
  };
  */

  // Commented out to fix unused variable linting error
  /*
  const selectEmbedCode = () => {
    if (embedCodeRef.current) {
      const range = document.createRange();
      range.selectNodeContents(embedCodeRef.current);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);

        // Automatically copy the selected text
        navigator.clipboard.writeText(embedCode);
        toast.success(t('common:share.copied'));
      }
    }
  };
  */

  const selectAndCopyLink = () => {
    handleCopyLink();
  };

  // const toggleEmbed = () => {
  //   setShowEmbed(!showEmbed);
  // };

  // Reset copy text when modal closes or language changes
  useEffect(() => {
    if (!show) {
      setCopyText(t('common:share.copy'));
      setActiveField(false);
    }
  }, [show, t]);

  return (
    <Dialog open={show} onClose={onClose} className="relative z-50">
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-gray-700 bg-opacity-75"
        aria-hidden="true"
      />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-[15px] bg-white p-6 shadow-xl transition-all scale-100 opacity-100">
            <div className="flex items-center justify-between border-b border-[#ebedf9] pb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {t('common:share.title')}
              </h2>
              <button
                onClick={onClose}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-[#f2f3fb] hover:bg-[#ebedf9] text-[#878787] transition-all"
              >
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-5">
              <p className="text-base text-gray-700 mb-4">
                {t('common:share.shareVia')}
              </p>

              {/* Social share icons */}
              <div className="grid grid-cols-6 gap-4 mb-8 px-1">
                <FacebookShareButton
                  url={shareUrl}
                  className="focus:outline-none"
                >
                  <div className="w-[50px] h-[50px] rounded-full border border-[#b7d4fb] text-[#1877F2] flex items-center justify-center hover:bg-[#1877F2] hover:text-white hover:border-transparent transition-all group">
                    <FontAwesomeIcon
                      icon={faFacebookF}
                      className="w-5 h-5 transform group-hover:scale-125 transition-transform"
                    />
                  </div>
                </FacebookShareButton>

                <TwitterShareButton
                  url={shareUrl}
                  title={contentTitle}
                  className="focus:outline-none"
                >
                  <div className="w-[50px] h-[50px] rounded-full border border-[#e0e0e0] text-[#000000] flex items-center justify-center hover:bg-[#000000] hover:text-white hover:border-transparent transition-all group">
                    <FontAwesomeIcon
                      icon={faXTwitter}
                      className="w-5 h-5 transform group-hover:scale-125 transition-transform"
                    />
                  </div>
                </TwitterShareButton>

                <LinkedinShareButton
                  url={shareUrl}
                  title={contentTitle}
                  className="focus:outline-none"
                >
                  <div className="w-[50px] h-[50px] rounded-full border border-[#c2d8f0] text-[#0077b5] flex items-center justify-center hover:bg-[#0077b5] hover:text-white hover:border-transparent transition-all group">
                    <FontAwesomeIcon
                      icon={faLinkedinIn}
                      className="w-5 h-5 transform group-hover:scale-125 transition-transform"
                    />
                  </div>
                </LinkedinShareButton>

                <WhatsappShareButton
                  url={shareUrl}
                  title={contentTitle}
                  className="focus:outline-none"
                >
                  <div className="w-[50px] h-[50px] rounded-full border border-[#bef4d2] text-[#25D366] flex items-center justify-center hover:bg-[#25D366] hover:text-white hover:border-transparent transition-all group">
                    <FontAwesomeIcon
                      icon={faWhatsapp}
                      className="w-5 h-5 transform group-hover:scale-125 transition-transform"
                    />
                  </div>
                </WhatsappShareButton>

                <TelegramShareButton
                  url={shareUrl}
                  title={contentTitle}
                  className="focus:outline-none"
                >
                  <div className="w-[50px] h-[50px] rounded-full border border-[#b3e6ff] text-[#0088cc] flex items-center justify-center hover:bg-[#0088cc] hover:text-white hover:border-transparent transition-all group">
                    <FontAwesomeIcon
                      icon={faTelegramPlane}
                      className="w-5 h-5 transform group-hover:scale-125 transition-transform"
                    />
                  </div>
                </TelegramShareButton>

                <EmailShareButton
                  url={shareUrl}
                  subject={contentTitle}
                  body={`${t('common:share.check_out')}: ${contentTitle}`}
                  className="focus:outline-none"
                >
                  <div className="w-[50px] h-[50px] rounded-full border border-[#d9e3ea] text-[#718096] flex items-center justify-center hover:bg-[#718096] hover:text-white hover:border-transparent transition-all group">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="w-5 h-5 transform group-hover:scale-125 transition-transform"
                    />
                  </div>
                </EmailShareButton>

                {/* Comment out embed button */}
                {/* <button onClick={toggleEmbed} className="focus:outline-none">
                  <div
                    className={`w-[50px] h-[50px] rounded-full border ${
                      showEmbed
                        ? 'bg-[#878787] text-white border-transparent'
                        : 'border-[#d1d1d1] text-[#878787] hover:bg-[#878787] hover:text-white hover:border-transparent'
                    } flex items-center justify-center transition-all group`}
                  >
                    <FontAwesomeIcon
                      icon={faCode}
                      className="w-5 h-5 transform group-hover:scale-125 transition-transform"
                    />
                  </div>
                </button> */}
              </div>

              {/* Comment out embed code section */}
              {/* {showEmbed && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-base font-medium text-gray-700">
                      {t('common:share.embedCode')}
                    </h3>
                    <button
                      onClick={handleCopyEmbed}
                      className="text-accent font-medium text-sm"
                    >
                      {t('common:share.copyCode')}
                    </button>
                  </div>
                  <pre
                    ref={embedCodeRef}
                    onClick={selectEmbedCode}
                    className="bg-white p-3 no-scrollbar rounded text-sm text-gray-600 whitespace-pre-wrap break-words border border-gray-200 cursor-pointer font-sans"
                  >
                    {embedCode}
                  </pre>
                </div>
              )} */}

              {/* Copy link section */}
              <div className="mt-4">
                <p className="text-base text-gray-700 mb-3">
                  {t('common:share.copyLink')}
                </p>
                <div
                  className={`flex h-[45px] border rounded-[4px] items-center ${
                    activeField ? 'border-accent' : 'border-[#e1e1e1]'
                  }`}
                >
                  <input
                    ref={linkInputRef}
                    type="text"
                    value={shareUrl}
                    readOnly
                    onClick={selectAndCopyLink}
                    className="flex-grow text-black h-full border-none border-transparent focus:border-transparent focus:ring-0 text-[15px] bg-transparent cursor-pointer"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-1 mr-1 bg-accent text-white rounded-[4px] text-sm font-medium transition-all hidden sm:block"
                  >
                    {copyText}
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="w-10 h-8 mr-1 bg-accent text-white rounded-[4px] flex items-center justify-center transition-all sm:hidden"
                  >
                    <FontAwesomeIcon icon={faCopy} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ShareModal;
